<!--Copyright (c) 2016 Jeff Sagal & vue-select contributors-->

<!--Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:-->

<!--The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.-->

<!--THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.-->
<template>
  <div
    :id="id"
    class="dropdown tc-select"
    :class="dropdownClasses"
  >
    <div
      :id="`${id}-toggle`"
      ref="toggle"
      class="d-flex flex-row align-items-center justify-content-left"
      :class="['dropdown-toggle', 'clearfix']"
      @mousedown.prevent="toggleDropdown"
    >
      <slot name="icon" />

      <div
        v-for="option in valueAsArray"
        :key="option.index"
        class="selected-tag"
      >
        <slot
          name="selected-option"
          v-bind="option"
        >
          {{ getOptionLabel(option) }}
        </slot>
      </div>

      <input
        :id="inputId"
        ref="search"
        v-model="search"
        type="search"
        class="form-control"
        autocomplete="false"
        :disabled="disabled"
        :placeholder="searchPlaceholder"
        :tabindex="tabindex"
        readonly="true"
        @keydown.delete="maybeDeleteValue"
        @keyup.esc="onEscape"
        @blur="onSearchBlur"
        @focus="onSearchFocus"
      >

      <span
        v-if="!noDrop"
        ref="openIndicator"
        class="open-indicator mr-2"
      />
    </div>

    <transition :name="transition">
      <ul
        v-if="dropdownOpen"
        ref="dropdownMenu"
        class="dropdown-menu"
      >
        <li
          v-for="(option, index) in filteredOptions"
          :id="`option-${index}`"
          :key="index"
          class="d-flex flex-column justify-content-center"
          @mousedown.prevent="select(option)"
        >
          <a>
            <slot
              name="option"
              v-bind="option"
            >
              {{ getOptionLabel(option) }}
            </slot>
          </a>
        </li>
        <li
          v-if="!filteredOptions.length"
          class="no-options d-flex flex-column justify-content-center"
        >
          <slot name="no-options">
            Sorry, no matching options.
          </slot>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: 'select-dropdown'
    },
    value: {
      type: Object,
      required: false,
      default: null
    },
    options: {
      type: Array,
      default () {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    transition: {
      type: String,
      default: 'fade'
    },
    clearSearchOnSelect: {
      type: Boolean,
      default: true
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: 'label'
    },
    getOptionLabel: {
      type: Function,
      default (option) {
        if (typeof option === 'object') {
          if (this.label && option[this.label]) {
            return option[this.label]
          }
        }
        return option
      }
    },
    onChange: {
      type: Function,
      default: function (val) {
        this.$emit('input', val)
      }
    },
    taggable: {
      type: Boolean,
      default: false
    },
    tabindex: {
      type: Number,
      default: null
    },
    pushTags: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: true
    },
    createOption: {
      type: Function,
      default (newOption) {
        if (typeof this.mutableOptions[0] === 'object') {
          newOption = { [this.label]: newOption }
        }
        this.$emit('option:created', newOption)
        return newOption
      }
    },
    resetOnOptionsChange: {
      type: Boolean,
      default: false
    },
    noDrop: {
      type: Boolean,
      default: false
    },
    inputId: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      search: '',
      open: false,
      mutableValue: null,
      mutableOptions: []
    }
  },
  computed: {
    dropdownClasses () {
      return {
        open: this.dropdownOpen,
        single: true,
        searching: this.searching,
        loading: this.mutableLoading,
        disabled: this.disabled
      }
    },
    clearSearchOnBlur () {
      return this.clearSearchOnSelect
    },
    searching () {
      return !!this.search
    },
    dropdownOpen () {
      return this.noDrop ? false : this.open && !this.mutableLoading
    },
    searchPlaceholder () {
      if (this.isValueEmpty && this.placeholder) {
        return this.placeholder
      } else {
        return null
      }
    },
    filteredOptions () {
      if (!this.filterable && !this.taggable) {
        return this.mutableOptions.slice()
      }
      const options = this.mutableOptions.filter((option) => {
        if (typeof option === 'object' && option.hasOwnProperty(this.label)) {
          return option[this.label].toLowerCase().indexOf(this.search.toLowerCase()) > -1
        } else if (typeof option === 'object' && !option.hasOwnProperty(this.label)) {
          return console.warn('Lablels for options are required')
        }
        return option.toLowerCase().indexOf(this.search.toLowerCase()) > -1
      })
      if (this.taggable && this.search.length && !this.optionExists(this.search)) {
        options.unshift(this.search)
      }
      return options
    },
    isValueEmpty () {
      if (this.mutableValue) {
        if (typeof this.mutableValue === 'object') {
          return !Object.keys(this.mutableValue).length
        }
        return !this.mutableValue.length
      }

      return true
    },
    valueAsArray () {
      return [this.mutableValue]
    }
  },
  watch: {
    value (newVal) {
      this.mutableValue = newVal
    },
    mutableValue (val, old) {
      if (this.onChange && val !== old) {
        this.onChange(val)
      }
    },
    options (val) {
      this.mutableOptions = val
    },
    mutableOptions () {
      if (!this.taggable && this.resetOnOptionsChange) {
        this.mutableValue = null
      }
    }
  },
  created () {
    this.mutableValue = this.value
    this.mutableOptions = this.options.slice(0)
    this.mutableLoading = this.loading

    this.$on('option:created', this.maybePushTag)
  },
  methods: {
    select (option) {
      if (this.taggable && !this.optionExists(option)) {
        option = this.createOption(option)
      }

      this.mutableValue = option

      this.onAfterSelect(option)
    },
    deselect (option) {
      this.mutableValue = null
    },
    clearSelection () {
      this.mutableValue = null
    },
    onAfterSelect (option) {
      if (this.closeOnSelect) {
        this.open = !this.open
        this.$refs.search.blur()
      }

      if (this.clearSearchOnSelect) {
        this.search = ''
      }
    },
    toggleDropdown (e) {
      if (this.open) {
        this.$refs.search.blur()
      } else {
        if (!this.disabled) {
          this.open = true
          this.$refs.search.focus()
        }
      }
    },
    isOptionSelected (option) {
      return this.mutableValue === option
    },
    onEscape () {
      if (!this.search.length) {
        this.$refs.search.blur()
      } else {
        this.search = ''
      }
    },
    onSearchBlur () {
      if (this.clearSearchOnBlur) {
        this.search = ''
      }
      this.open = false
      this.$emit('search:blur')
    },
    onSearchFocus () {
      this.open = true
      this.$emit('search:focus')
    },
    maybeDeleteValue () {
      if (!this.$refs.search.value.length && this.mutableValue) {
        this.mutableValue = null
      }
    },
    optionExists (option) {
      let exists = false

      this.mutableOptions.forEach(opt => {
        if (typeof opt === 'object' && opt[this.label] === option) {
          exists = true
        } else if (opt === option) {
          exists = true
        }
      })

      return exists
    },
    maybePushTag (option) {
      if (this.pushTags) {
        this.mutableOptions.push(option)
      }
    }
  }
}
</script>

<style lang="scss">
  @import "~@/assets/scss/variables";
  @import "~@/assets/scss/mixins";

  .tc-select {
    position: relative;
    border-radius: 2px;
  }

  .tc-select .open-indicator {
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 7px solid var(--t-color-border);
  }

  .tc-select .dropdown-toggle {
    display: block;
    padding: 0;
    background: none;
    border: 1px solid var(--t-color-border);
    white-space: normal;
  }

  .tc-select .dropdown-toggle:after {
    visibility: hidden;
    display: block;
    font-size: 0;
    content: " ";
    clear: both;
    height: 0;
    border-bottom: none;
  }

  .tc-select.open .dropdown-toggle {
    border-bottom-color: transparent;
  }

  .tc-select .dropdown-menu {
    @include gotham-book();
    display:block;
    position: absolute;
    left: 0;
    z-index: 1000;
    margin: 0;
    padding: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 300px;
    box-shadow: 0 0 2px 0 rgba(0,0,0,.12), 0 2px 2px 0 rgba(0,0,0,.24);
    border-top: none;
    text-align: left;
    list-style: none;
    background: var(--t-color-surface);
  }

  .tc-select .no-options {
    text-align: center;
  }

  .tc-select .selected-tag {
    @include gotham-book();
    color: var(--t-color-text);
    background: var(--t-color-surface);
    border: 1px solid var(--t-color-border);
    height: 100%;
    width: 100%;
    padding-left: 8px;
  }

  .tc-select.single .selected-tag {
    background-color: transparent;
    border-color: transparent;
  }

  .tc-select input[type="search"]{
    height: 50px;
    display: inline-block;
    border: none;
    outline: none;
    margin: 0;
    padding: 0 8px;
    max-width: 1px;
    background: none;
    position: relative;
    box-shadow: none;
  }

  .tc-select li {
    height: 45px;
    border-bottom: solid 1px var(--t-color-border);
  }

  .tc-select li > a {
    display: block;
    padding: 0 8px;
    clear: both;
    color: var(--t-color-text);
    white-space: nowrap;
  }
  .tc-select li:hover {
    cursor: pointer;
  }

  .tc-select.disabled .dropdown-toggle,
  .tc-select.disabled .dropdown-toggle .clear,
  .tc-select.disabled .dropdown-toggle input,
  .tc-select.disabled .open-indicator {
    cursor: not-allowed;
    background-color: $input-disabled-bg;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .15s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
</style>
